import { FC, useEffect, useState } from 'react';
import { AxiosResponse } from 'axios';

import countryService from '../../services/countryService';
import toastService from '../../services/toastService';

import * as models from '../../utils/models';
import * as utils from '../../utils/utilities';

function withCountries(Component: any): FC<any> {
	return function WithCountries(props): JSX.Element {
		const [countries, setCountries] = useState<models.SelectOption[]>([]);
		const [cities, setCities] = useState<models.SelectOption[]>([]);
		const [states, setStates] = useState<models.SelectOption[]>([]);
		const [isLoading, setIsLoading] = useState<boolean>(false);

		const handleSetCountriesFromResponse = ({ data }: AxiosResponse) => {
			const countries = data.map(utils.transformCountry);
			setCountries(countries);
		};

		const handleSetCitiesFromResponse = ({ data }: AxiosResponse) => {
			const cities = data.map(utils.transformCityToOption);
			setCities(cities);
		};

		const handleSetStatesFromResponse = ({ data }: AxiosResponse) => {
			const mappedStates = data.states.map(utils.transformStateToOption);
			setStates(mappedStates);
		};

		const fetchCountries = async () => {
			try {
				setIsLoading(true);
				const result = await countryService.getCountries();
				setIsLoading(false);

				if (result.data.error) return toastService.error(result.data.msg);
				handleSetCountriesFromResponse(result.data);
			} catch (ex: any) {
				const cachedData = utils.getCachedDataFromException(ex);
				if (cachedData) {
					if (cachedData.error) toastService.error(cachedData.msg);
					else handleSetCountriesFromResponse(cachedData);
				}
			}
		};

		const fetchCitiesInState = async (country: string, state: string) => {
			try {
				const result = await countryService.getCitiesInState(country, state);
				if (result.data.error) return toastService.error(result.data.msg);

				handleSetCitiesFromResponse(result.data);
			} catch (ex: any) {
				const cachedData = utils.getCachedDataFromException(ex);
				if (cachedData) {
					if (cachedData.error) toastService.error(cachedData.msg);
					else handleSetCitiesFromResponse(cachedData);
				}
			}
		};

		const fetchStatesInCountry = async (country: string) => {
			try {
				const result = await countryService.getStatesInCountry(country);
				if (result.data.error) return toastService.error(result.data.msg);

				handleSetStatesFromResponse(result.data);
			} catch (ex: any) {
				const cachedData = utils.getCachedDataFromException(ex);
				if (cachedData) {
					if (cachedData.error) toastService.error(cachedData.msg);
					else handleSetStatesFromResponse(cachedData);
				}
			}
		};

		useEffect(() => {
			fetchCountries();

			// eslint-disable-next-line
		}, []);

		return (
			<Component
				countries={countries}
				cities={cities}
				states={states}
				countriesLoading={isLoading}
				getStatesInCountry={fetchStatesInCountry}
				getCitiesInState={fetchCitiesInState}
				refreshCountries={fetchCountries}
				{...props}
			/>
		);
	};
}

export default withCountries;
